import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AppSettingsService } from '~/core/services/appsettings.service';
import { AppSettings } from '~/core/models/appsettings';
import { TabNavigationService } from '~/services/tab-navigation.service';
import { ConfirmationService } from '~/services/confirmation.service';

import { routes } from '~/classes/routes/app.routing';
import { WellsApiService } from '~/services/wells-api.service';

export class NavItem {
    public title: string = "";
    public icon: string = "";
    public imageIcon?: string = "";
    public Id?: string = "";
    public Count?: number = 0;
    public url: string = "";
    public Selected?: boolean = false;
    public roles?: string[] = [];
    public visible?: boolean = false;
}

@Component({
    selector: 'nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.scss'],
    standalone: false
})

export class NavComponent {
    @Input() expand: boolean = false;
    @Output() setLayoutClass = new EventEmitter();
    @Output() reloadPage = new EventEmitter();
    @Output() showSearch: EventEmitter<boolean> = new EventEmitter();
    @Output() uploadModalRequested = new EventEmitter<void>();

    public appIcon: string = "web_asset";
    public appTitle: string = "Application Mode";
    public selectedBU: string = "";

    public menuIcon: string = "keyboard_double_arrow_right";
    public activeItem: NavItem = new NavItem();
    public isBuSelected: boolean = false;
    public isHomeActive: boolean = false;
    public isUploadActive: boolean = false;
    selectedBU$ = this.wellsApiService.selectedBU$;
    uploadModal$ = this.tabNavigationService.uploadModal$;

    public navItems: any = [];
    public navSubItems: any = [];

    private _routes = [...routes];
    private collapse: boolean = false;
    private search: boolean = false;
    private isInWellView: boolean = false;

    constructor(
        private wellsApiService: WellsApiService,
        public router: Router,
        private appSettingsService: AppSettingsService,
        private tabNavigationService: TabNavigationService,
        private confirmationService: ConfirmationService
    ) { }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        this.selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        this.checkBuSelection();

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.checkBuSelection();
        });

        await this.getRoutes();

        if (this.router?.url) {
            let routeUrl = this.router.url.split("?")[0];

            this.setActiveItem(this.navItems, routeUrl);
            this.setActiveItem(this.navSubItems, routeUrl);
        }
        
        // Subscribe to tab changes to update active item
        this.tabNavigationService.activeTab$.subscribe(tab => {
            // Only update active item if we're not in individual well view
            if (!this.isInWellView) {
                const tabItem = this.navItems.find((item: NavItem) => 
                    item.title.toLowerCase() === tab.toLowerCase()
                );
                if (tabItem) {
                    this.activeItem = tabItem;
                }
            }
        });
        
        // Subscribe to well view state changes
        this.tabNavigationService.isInWellView$.subscribe(isInView => {
            this.isInWellView = isInView;
            
            // Clear active item when entering individual well view
            if (isInView) {
                this.activeItem = new NavItem();
            } else {
                // Restore active item based on current tab when exiting individual well view
                const currentTab = this.tabNavigationService.getActiveTab();
                const tabItem = this.navItems.find((item: NavItem) => 
                    item.title.toLowerCase() === currentTab.toLowerCase()
                );
                if (tabItem) {
                    this.activeItem = tabItem;
                }
            }
        });
        
        // Subscribe to upload modal state to clear active item when upload is open
        this.tabNavigationService.uploadModal$.subscribe(isOpen => {
            this.isUploadActive = isOpen;
            if (isOpen) {
                // Clear active item when upload modal is open
                this.activeItem = new NavItem();
            } else {
                // Restore active item based on current tab when upload closes
                if (!this.isInWellView) {
                    const currentTab = this.tabNavigationService.getActiveTab();
                    const tabItem = this.navItems.find((item: NavItem) => 
                        item.title.toLowerCase() === currentTab.toLowerCase()
                    );
                    if (tabItem) {
                        this.activeItem = tabItem;
                    }
                }
            }
        });
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public toggleMenu(collapse: boolean = false) {
        this.collapse = collapse;
        this.menuIcon = collapse ? "keyboard_double_arrow_left" : "keyboard_double_arrow_right";

        this.setLayoutClass.emit();
    }

    public navigateTo(item: NavItem) {
        if (!this.isBuSelected) return;

        const isOnApproveRoute = this.isOnApproveRoute();
        let allowNavigate: boolean = true;
        let storage = sessionStorage.getItem("__allowNavigate__");

        // Show confirmation if on approve route or if there are unsaved changes
        if (isOnApproveRoute || (storage && storage === "false")) {
            this.confirmationService.show({
                title: 'Exit Page',
                message: 'Are you sure you want to exit this page?',
                warningText: isOnApproveRoute ? 'Any unsaved changes will be lost.' : 'All your changes will be lost.',
                confirmText: 'Exit',
                cancelText: 'Cancel',
                type: 'warning',
                confirmCallback: () => {
                    sessionStorage.removeItem("__allowNavigate__");
                    this.proceedNavigation(item);
                },
                cancelCallback: () => {
                    // Stay on current page
                }
            });
            return;
        }

        if (this.collapse) {
            this.collapse = false;
            this.setLayoutClass.emit();
        }

        if (allowNavigate) {
            // For Wells, update both the tab and the route
            if (item.title === 'Wells') {
                this.tabNavigationService.closeUploadModal();
                this.tabNavigationService.setActiveTab('wells');
                this.tabNavigationService.triggerResetWells();
                this.activeItem = item;
                this.router.navigate(['/wells']);
            } else {
                this.router.navigateByUrl(item.url);
                this.activeItem = item;
            }
        }
    }

    private proceedNavigation(item: NavItem): void {
        if (this.collapse) {
            this.collapse = false;
            this.setLayoutClass.emit();
        }

        // For Wells, update both the tab and the route
        if (item.title === 'Wells') {
            this.tabNavigationService.closeUploadModal();
            this.tabNavigationService.setActiveTab('wells');
            this.tabNavigationService.triggerResetWells();
            this.activeItem = item;
            this.router.navigate(['/wells']);
        } else {
            this.router.navigateByUrl(item.url);
            this.activeItem = item;
        }
    }

    public toggleFullscreen() {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            this.appIcon = "iframe";
            this.appTitle = "Web Mode";
        } else {
            document.exitFullscreen();
            this.appIcon = "web_asset";
            this.appTitle = "Application Mode";
        }
    }

    public onNavItemMouseMove(event: MouseEvent): void {
        if (this.expand) return; // Only show tooltip when navbar is collapsed
        
        const element = event.currentTarget as HTMLElement;
        if (!element) return;
        
        const tooltip = element.querySelector('.title') as HTMLElement;
        
        if (tooltip) {
            // Position tooltip near cursor
            const tooltipX = event.clientX + 10; // 10px offset from cursor
            const tooltipY = event.clientY + 10;
            
            tooltip.style.setProperty('--tooltip-x', `${tooltipX}px`);
            tooltip.style.setProperty('--tooltip-y', `${tooltipY}px`);
        }
    }

    /**
     * Check if a navigation item is active
     * Used for styling the active nav item
     */
    public isNavItemActive(item: NavItem): boolean {
        if (!this.isBuSelected || this.isUploadActive) {
            return false;
        }

        // Wells item: check if URL starts with /wells
        if (item.title === 'Wells') {
            return this.router.url.startsWith('/wells') && !this.router.url.includes('/wells/');
        }

        // Other items: check active item
        return this.activeItem && item.title === this.activeItem.title;
    }

    /**
     * Check if a sub-navigation item is active
     * Used for styling the active sub-nav item
     */
    public isSubItemActive(subItem: NavItem): boolean {
        if (!this.isBuSelected || !this.activeItem) {
            return false;
        }

        return subItem.title === this.activeItem.title;
    }

    /**
     * Check if user is on the approval/review route
     * Used to show confirmation when navigating away
     */
    private isOnApproveRoute(): boolean {
        const currentUrl = this.router.url.split('?')[0];
        return currentUrl.includes('/review/approve') || currentUrl.includes('/review') && currentUrl.includes('/approve');
    }

    public navigateToHome() {
        // Home is always accessible
        const isOnApproveRoute = this.isOnApproveRoute();
        let allowNavigate: boolean = true;
        let storage = sessionStorage.getItem("__allowNavigate__");

        if (isOnApproveRoute || (storage && storage === "false")) {
            this.confirmationService.show({
                title: 'Exit Page',
                message: 'Are you sure you want to go to Home?',
                warningText: isOnApproveRoute ? 'Any unsaved changes will be lost.' : 'All your changes will be lost.',
                confirmText: 'Go to Home',
                cancelText: 'Cancel',
                type: 'warning',
                confirmCallback: () => {
                    sessionStorage.removeItem("__allowNavigate__");
                    this.proceedHomeNavigation();
                },
                cancelCallback: () => {
                    // Stay on current page
                }
            });
            return;
        }

        if (this.collapse) {
            this.collapse = false;
            this.setLayoutClass.emit();
        }

        if (allowNavigate) {
            this.tabNavigationService.closeUploadModal();
            this.tabNavigationService.setInWellView(false);
            this.tabNavigationService.setActiveTab('wells');
            this.tabNavigationService.triggerResetWells();
            // Clear BU selection and navigate to landing page
            sessionStorage.removeItem('selectedBU');
            this.isBuSelected = false;
            this.isUploadActive = false;
            this.activeItem = new NavItem();
            this.checkBuSelection(); // Immediately update icon state
            this.router.navigate(['/']);
        }
    }

    private proceedHomeNavigation(): void {
        if (this.collapse) {
            this.collapse = false;
            this.setLayoutClass.emit();
        }

        this.tabNavigationService.closeUploadModal();
        this.tabNavigationService.setInWellView(false);
        this.tabNavigationService.setActiveTab('wells');
        this.tabNavigationService.triggerResetWells();
        // Clear BU selection and navigate to landing page
        sessionStorage.removeItem('selectedBU');
        this.isBuSelected = false;
        this.isUploadActive = false;
        this.activeItem = new NavItem();
        this.checkBuSelection(); // Immediately update icon state
        this.router.navigate(['/']);
    }

    public openUploadModal() {
        if (!this.isBuSelected) return;
        
        // Don't allow opening upload from within upload route
        if (this.router.url === '/upload') return;

        const isOnApproveRoute = this.isOnApproveRoute();
        let allowNavigate: boolean = true;
        let storage = sessionStorage.getItem("__allowNavigate__");

        if (isOnApproveRoute || (storage && storage === "false")) {
            this.confirmationService.show({
                title: 'Exit Page',
                message: 'Are you sure you want to open Upload Excel?',
                warningText: isOnApproveRoute ? 'Any unsaved changes will be lost.' : 'All your changes will be lost.',
                confirmText: 'Continue',
                cancelText: 'Cancel',
                type: 'warning',
                confirmCallback: () => {
                    sessionStorage.removeItem("__allowNavigate__");
                    this.proceedOpenUploadModal();
                },
                cancelCallback: () => {
                    // Stay on current page
                }
            });
            return;
        }

        if (allowNavigate) {
            this.proceedOpenUploadModal();
        }
    }

    private proceedOpenUploadModal(): void {
        // If not on wells page, navigate there first
        if (!this.router.url.startsWith('/wells') || this.router.url.includes('/wells/')) {
            this.tabNavigationService.setInWellView(false);
            this.router.navigate(['/wells']).then(() => {
                // Open upload type modal after navigation completes
                setTimeout(() => {
                    this.tabNavigationService.openUploadTypeModal();
                }, 100);
            });
        } else {
            // Already on wells list page, just open upload type modal
            this.tabNavigationService.openUploadTypeModal();
        }
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private async getRoutes() {
        this.navItems = [];
        this.navSubItems = [];

        await this.appSettingsService.getSettings().then((appSettings: AppSettings) => {
            this._routes.forEach((route: any) => {
                if (route.isNav) {
                    let addItem: boolean = false;

                    if (route.roles) {
                        if (route.roles.some((r: any) => appSettings.roles.includes(r))) {
                            addItem = true;
                        }
                    } else {
                        addItem = true;
                    }

                    if (addItem) {
                        this.navItems.push(
                            {
                                "title": route.title,
                                "icon": route.icon,
                                "imageIcon": route.imageIcon,
                                "url": route.path
                            }
                        )

                        this.getSubRoutes(route, appSettings.roles);
                    }
                }
            })
        });
    }

    private getSubRoutes(parent: any, roles: any) {
        this._routes.forEach((route: any) => {
            if (route.isSubNav && route.path.startsWith(`${parent.path}/`)) {
                let addItem: boolean = false;

                if (route.roles) {
                    if (route.roles.some((r: any) => roles.includes(r))) {
                        addItem = true;
                    }
                } else {
                    addItem = true;
                }

                if (addItem) {
                    this.navSubItems.push(
                        {
                            "parent": parent.title,
                            "title": route.title,
                            "icon": route.icon,
                            "url": route.path
                        }
                    )
                }
            }
        });
    }

    private setActiveItem(items: any, routeUrl: any) {
        for (let item of items) {
            if (`/${item.url}` === routeUrl) {
                this.activeItem = item;
                break;
            }
        }
    }

    private checkBuSelection() {
        // Check if we are on the root path (BU selection page)
        const currentUrl = this.router.url.split('?')[0];
        this.isHomeActive = currentUrl === '/';
        this.isBuSelected = !!sessionStorage.getItem('selectedBU');
    }
}
